import { Button, CircularProgress, InputAdornment, TextField } from '@material-ui/core';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import clsx from 'clsx';
import { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { AssignmentResourceFilter } from '../assignmentVisibility';
import { useStyles } from '../styles';
import { ResourceResultRow, useResourceResults } from '../useResourceResults';

interface ResourceResultsTabProps {
  user: any;
  assignmentFilter?: AssignmentResourceFilter | null;
  assignmentFilterReady?: boolean;
}

const ResourceTable: FC<{
  title: string;
  rows: ResourceResultRow[];
  nameHeader: string;
  dateHeader: string;
  showAssignedDate?: boolean;
  actionLabel: string;
  openInNewTab?: boolean;
}> = ({
  title,
  rows,
  nameHeader,
  dateHeader,
  showAssignedDate = false,
  actionLabel,
  openInNewTab = false,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const handleView = (path: string, rowOpensInNewTab?: boolean) => {
    if (openInNewTab || rowOpensInNewTab) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }
    history.push(path);
  };

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className={classes.resultsSection}>
      <h2 className={classes.resultsSectionTitle}>{title}</h2>
      <div className={classes.tableWrap}>
        <table className={clsx(classes.table, classes.resourceTable)}>
          <colgroup>
            <col className={classes.colName} />
            {showAssignedDate ? <col className={classes.colDate} /> : null}
            <col className={classes.colDate} />
            <col className={classes.colStatus} />
            <col className={classes.colAction} />
          </colgroup>
          <thead>
            <tr>
              <th className={classes.thLeft}>{nameHeader}</th>
              {showAssignedDate ? (
                <th className={classes.thCenter}>Date d&apos;assignation</th>
              ) : null}
              <th className={classes.thCenter}>{dateHeader}</th>
              <th className={classes.thCenter}>{showAssignedDate ? 'Lecture' : 'Statut'}</th>
              <th className={classes.thCenter}>Résultat</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id}>
                <td className={classes.nameCell} title={row.name}>
                  {row.name}
                </td>
                {showAssignedDate ? <td className={classes.adminCell}>{row.assignedAt}</td> : null}
                <td className={classes.adminCell}>
                  {showAssignedDate ? row.consultedAt : row.assignedAt}
                </td>
                <td className={classes.adminCell}>
                  <span
                    className={clsx(
                      classes.lectureTag,
                      row.isRead ? classes.lectureRead : classes.lectureUnread
                    )}>
                    {row.isRead ? 'Lu' : 'Non lu'}
                  </span>
                </td>
                <td className={classes.resultCell}>
                  {row.viewPath ? (
                    <Button
                      className={classes.resultBtn}
                      aria-label={`${row.actionLabel || actionLabel} ${row.name}`}
                      onClick={() => handleView(row.viewPath as string, row.openInNewTab)}>
                      {row.actionLabel || actionLabel}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ResourceResultsTab: FC<ResourceResultsTabProps> = ({
  user,
  assignmentFilter = null,
  assignmentFilterReady = true,
}) => {
  const classes = useStyles();
  const page = useResourceResults(user, assignmentFilter, assignmentFilterReady);
  const hasVisibleRows =
    page.ebookRows.length > 0 || page.customEbookRows.length > 0 || page.gameRows.length > 0;

  return (
    <>
      <div className={classes.resultsToolbar}>
        <TextField
          className={classes.resultsSearch}
          variant="outlined"
          placeholder="Rechercher"
          value={page.search}
          onChange={event => page.setSearch(event.target.value)}
          aria-label="Rechercher"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MagnifyingGlassIcon size={20} color="#8B90A5" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {page.loading ? (
        <div className={classes.loader}>
          <CircularProgress />
        </div>
      ) : hasVisibleRows ? (
        <>
          <ResourceTable
            title="Ebooks majeurs"
            rows={page.ebookRows}
            nameHeader="Nom de l'ebook"
            dateHeader="Dernière date de consultation"
            showAssignedDate={true}
            actionLabel="Voir l'ebook"
          />
          <ResourceTable
            title="Ebooks mineurs"
            rows={page.customEbookRows}
            nameHeader="Nom de l'ebook"
            dateHeader="Dernière date de consultation"
            showAssignedDate={true}
            actionLabel="Voir l'ebook"
          />
          <ResourceTable
            title="Serious games"
            rows={page.gameRows}
            nameHeader="Nom du serious game"
            dateHeader="Date"
            actionLabel="Voir la synthèse"
            openInNewTab={false}
          />
        </>
      ) : (
        <p className={classes.emptyTab}>
          {page.search.trim() && page.hasAnyResult
            ? `Aucune ressource ne correspond à votre recherche « ${page.search.trim()} ».`
            : 'Aucune ressource pour ce bénéficiaire.'}
        </p>
      )}
    </>
  );
};

export default ResourceResultsTab;
